import { request } from '@/lib/api'

/**
 * Доступ к кадрам со стороны офиса (тикет #75, ТЗ §4.1).
 *
 * ## Почему отдельно от `card/api.ts`
 *
 * `card/api.ts` — универсальный слой каркаса #66: он читает и пишет **одну плоскую
 * строку** через механизм списков. Кадровая сущность плоской строкой не описывается: у
 * сотрудника есть история назначений, у отпуска — маршрут согласования и пересечения с
 * коллегами, у вакансии — воронка откликов. Показать статус заявки на отпуск без того,
 * кто ещё из отдела в эти дни отсутствует, значит показать руководителю ровно ту цифру,
 * за которой он и пришёл, без того, что она означает.
 *
 * ## Почему кадровые записи не редактируются формой
 *
 * Ни одна кадровая сущность не объявляет `importable` колонок в `ListCatalog`, и это
 * намеренно. У каждой своя причина, и все они принципиальны:
 *
 * - **Сотрудник**: приём порождает назначение, чек-лист адаптации и учётную запись, а
 *   строка, вставленная мимо `EmployeeService`, даёт сотрудника без всего этого.
 * - **Отпуск**: маршрут согласования строится из оргструктуры в момент подачи, и заявка,
 *   вставленная мимо `LeaveService`, — это отпуск, который никто не согласовывал.
 * - **KPI**: факт пересчитывается из источника, план сверяет сумму весов.
 *
 * Поэтому здесь только операции.
 *
 * ## Оклад и ПДн — два разных ограничения, не одно
 *
 * **Оклад** приходит `null` без полевого права `hr.employee.read.salary` — не нулём:
 * ноль читался бы как «оклада нет». Решает это сервер, а не экран: скрытое клиентом
 * значение всё равно видно в ответе API.
 *
 * **ПДн** (ИНН, СНИЛС, паспорт, личный телефон) в списочный каталог не внесены вовсе —
 * они лежат шифротекстом AES-GCM. Читаются отдельным вызовом {@link fetchPersonalData},
 * поштучно и с записью в аудит. Отдельный вызов, а не поле карточки, — именно чтобы
 * открытие карточки не означало расшифровку паспорта.
 *
 * ## Чего здесь нет — названо вслух
 *
 * - **Обёрток над обучением, адаптацией и кадровыми документами.** `TrainingService`,
 *   `OnboardingService` и `HrDocumentService` живут в `crm-hr` и покрыты тестами, но
 *   REST над ними в #68 не выводился — вызывать нечего. Экрана поэтому тоже нет:
 *   обёртка без эндпоинта выглядела бы рабочей возможностью.
 * - **Отзыва согласия из карточки кандидата.** Эндпоинт есть
 *   (`POST /candidates/{id}/revoke-consent`), и он здесь объявлен; экран его показывает
 *   на доске вакансии. Отдельной карточки кандидата нет — `hr.candidate` не объявлен в
 *   `ListCatalog`, и карточке #66 нечего читать без каталога колонок.
 * - **Создания подразделения.** `POST /departments` в #68 не выводился: есть только
 *   перемещение узла и чтение поддерева. Форма создания предлагала бы отказ сервера.
 *
 * ## Где вызываются операции
 *
 * Перевод, увольнение, расчёт и начисление премии и ручной факт вызываются с карточек на
 * дизайн-системе (Employee.dc.html, EmployeeKpi.dc.html): формы живут в шапке карточек.
 * Подача заявки на отсутствие ({@link submitLeave}) — экран `LeaveCreatePage`, кнопка
 * «Создать» списка отпусков: у новой заявки записи ещё нет, и карточке открываться не по
 * чему. Отмена ({@link cancelLeave}) — меню карточки заявки, под правом `hr.leave:update`
 * с областью вызывающего (#94).
 */

// ------------------------------------------------------------------ сотрудник

/**
 * Карточка сотрудника.
 *
 * `salary` и `currency` — `null` без полевого права `hr.employee.read.salary`. Это
 * решение сервера, и различать «нет права» от «оклад не назначен» экран не может: оба
 * приходят одинаково. Поэтому прочерк, а не ноль.
 */
export interface EmployeeCard {
  id: string
  organizationId: string | null
  personnelNumber: string | null
  fullName: string
  lastName: string
  firstName: string
  middleName: string | null
  workEmail: string | null
  workPhone: string | null
  departmentId: string | null
  positionId: string | null
  managerId: string | null
  hiredOn: string | null
  terminatedOn: string | null
  status: string
  salary: number | null
  currency: string | null
}

/**
 * Строка истории назначений.
 *
 * История, а не текущее состояние: перевод не затирает прошлые условия, потому что
 * расчёт зарплаты за прошлый период обязан считаться по тем условиям, что действовали
 * тогда. `current` приходит с сервера — вычислять его здесь по датам значило бы завести
 * второй ответ на вопрос «какое назначение действует».
 */
export interface AssignmentView {
  id: string
  employeeId: string
  departmentId: string | null
  /** Название отдела из приказа; нет — отдела нет в строке. */
  departmentName?: string | null
  positionId: string | null
  /** Название должности из справочника — на экране вместо идентификатора. */
  positionName?: string | null
  reason: string | null
  orderNumber: string | null
  effectiveFrom: string
  effectiveTo: string | null
  current: boolean
  salary: number | null
  currency: string | null
  employmentRate: number | null
}

/** Должность из справочника `GET /hr/positions` — для выбора при переводе. */
export interface PositionView {
  id: string
  code: string
  name: string
  grade?: string | null
}

export async function fetchPositions(signal?: AbortSignal): Promise<PositionView[]> {
  const body = await request<{ positions: PositionView[] }>('/hr/positions', { signal })
  return body.positions
}

/**
 * Расшифрованные ПДн.
 *
 * Отдельный вызов, а не поля карточки: открытие карточки сотрудника не должно означать
 * расшифровку паспорта. Каждое чтение пишется в аудит.
 */
export interface PersonalDataView {
  employeeId: string
  inn: string | null
  snils: string | null
  passport: string | null
  personalPhone: string | null
}

export function fetchEmployee(id: string, signal?: AbortSignal): Promise<EmployeeCard> {
  return request<EmployeeCard>(`/hr/employees/${id}`, { signal })
}

export async function fetchAssignments(
  id: string,
  signal?: AbortSignal,
): Promise<AssignmentView[]> {
  const body = await request<{ assignments: AssignmentView[] }>(
    `/hr/employees/${id}/assignments`,
    { signal },
  )
  return body.assignments
}

/**
 * Прямые подчинённые.
 *
 * Оргструктура снизу вверх: у сотрудника может не быть учётной записи, но подчинённые у
 * него быть могут — руководитель склада без логина всё равно руководитель.
 */
export async function fetchReports(id: string, signal?: AbortSignal): Promise<EmployeeCard[]> {
  const body = await request<{ employees: EmployeeCard[] }>(`/hr/employees/${id}/reports`, {
    signal,
  })
  return body.employees
}

/** Расшифровка ПДн. Отдельным действием и с записью в аудит — см. {@link PersonalDataView}. */
export function fetchPersonalData(
  id: string,
  signal?: AbortSignal,
): Promise<PersonalDataView> {
  return request<PersonalDataView>(`/hr/employees/${id}/personal-data`, { signal })
}

/**
 * Перевод: новые условия с даты.
 *
 * Возвращает **новую строку истории**, а не карточку: перевод не правит сотрудника, он
 * закрывает прошлое назначение днём раньше и открывает следующее. Расчёт за прошлый
 * период обязан считаться по тем условиям, что действовали тогда.
 *
 * Вид изменения (перевод, повышение, изменение оклада) выводит домен из того, что
 * фактически поменялось, и клиент его не присылает: они порождают разные приказы, и
 * назвать всё переводом значит сделать историю нечитаемой для того, кто их подшивает.
 */
export function transferEmployee(
  id: string,
  payload: {
    departmentId: string | null
    positionId: string | null
    salary: number | null
    currency: string | null
    effectiveFrom: string
    orderNumber: string | null
  },
): Promise<AssignmentView> {
  return request<AssignmentView>(`/hr/employees/${id}/transfer`, {
    method: 'POST',
    body: payload,
  })
}

/**
 * Увольнение.
 *
 * Право `delete`, а не `update`: увольнение закрывает трудовые отношения и отзывает
 * доступ, и давать его каждому, кто может править карточку, значит уравнять опечатку в
 * телефоне с прекращением договора.
 *
 * `lastDay` — последний рабочий день, а не дата приказа: по нему считается всё остальное.
 */
export function terminateEmployee(
  id: string,
  payload: { lastDay: string; orderNumber: string | null },
): Promise<EmployeeCard> {
  return request<EmployeeCard>(`/hr/employees/${id}/terminate`, {
    method: 'POST',
    body: payload,
  })
}

/** Прохождение испытательного срока. */
export function confirmProbation(id: string): Promise<EmployeeCard> {
  return request<EmployeeCard>(`/hr/employees/${id}/confirm-probation`, { method: 'POST' })
}

// ------------------------------------------------------------------ оргструктура

/**
 * Узел оргструктуры.
 *
 * `level` — глубина от корня, её считает сервер по материализованному пути. Считать её
 * здесь пересчётом `path` значило бы завести вторую реализацию обхода дерева, и разошлась
 * бы она молча — на отделе, чьё имя содержит разделитель.
 */
export interface DepartmentNode {
  id: string
  organizationId: string | null
  code: string | null
  name: string
  path: string
  level: number
  headEmployeeId: string | null
  costCenter: string | null
}

export async function fetchSubtree(
  id: string,
  signal?: AbortSignal,
): Promise<DepartmentNode[]> {
  const body = await request<{ subtree: DepartmentNode[] }>(`/hr/departments/${id}/subtree`, {
    signal,
  })
  return body.subtree
}

/**
 * Перенос узла оргструктуры.
 *
 * Переносится поддерево целиком — по нему ходит область видимости «своё подразделение»,
 * и узел, оторванный от детей, оставил бы половину компании вне чьей-либо видимости.
 * Это делает сервер; здесь только вызов.
 */
export async function moveDepartment(
  id: string,
  newParentId: string | null,
): Promise<DepartmentNode[]> {
  const body = await request<{ subtree: DepartmentNode[] }>(`/hr/departments/${id}/move`, {
    method: 'POST',
    body: { newParentId },
  })
  return body.subtree
}

// ------------------------------------------------------------------ отпуска

/** Заявка на отсутствие. */
export interface LeaveRequestCard {
  id: string
  employeeId: string
  type: string
  from: string
  to: string
  daysCount: number
  status: string
  comment: string | null
  /** Причина отказа (V53): обязательна при отказе, у прежних отказов её нет. */
  rejectionReason?: string | null
}

/**
 * Шаг маршрута согласования.
 *
 * Согласующий — это либо конкретная учётная запись (руководитель), либо роль (кадры).
 * Оба поля вместе не заполняются: роль это «кто-нибудь из», учётная запись — «именно он»,
 * и подменять одно другим нельзя.
 *
 * `approverUserId` — это `app_user`, а НЕ `employee`. Разные идентификаторы одного
 * человека: `approval_step.approver_id` ссылается на учётную запись, а `employee.manager_id`
 * — на сотрудника. У сотрудника учётной записи может не быть, и тогда шага руководителя в
 * маршруте просто нет.
 */
export interface RouteStepView {
  name: string
  order: number
  approverUserId: string | null
  approverRoleId: string | null
}

/**
 * Остаток отпуска.
 *
 * `carriedOver` отдельно от `entitled`: закон относится к перенесённым дням иначе, и
 * сумма не отвечает на вопрос, сколько дней этого года человек отгулял.
 */
export interface BalanceView {
  employeeId: string
  year: number
  type: string
  entitled: number
  used: number
  carriedOver: number
  remaining: number
}

/**
 * Покрытие подразделения на период.
 *
 * `thin` — сигнал, а не запрет: решает человек. Автоматический отказ в отпуске из-за
 * того, что треть отдела уже отсутствует, перенёс бы кадровое решение в арифметику,
 * которая не знает ни про подмены, ни про сезон.
 */
export interface CoverageView {
  departmentId: string
  headcount: number
  away: number
  present: number
  thin: boolean
}

/** День табеля. Строка на каждый день: пропуск неотличим от «никто не заполнил». */
export interface TimeEntryView {
  id: string
  employeeId: string
  day: string
  type: string
  hoursWorked: number
  comment: string | null
}

/**
 * Предпросмотр маршрута согласования.
 *
 * Тот же маршрут, что построится при подаче, — построенный тем же методом на сервере, а
 * не пересчитанный здесь по описанию: копия правила разошлась бы с оригиналом при первой
 * правке, и сотрудник увидел бы маршрут, по которому заявка не пойдёт.
 *
 * Передаётся **период**, а не число дней. Дни считает сервер по производственному
 * календарю — тем же счётом, каким их считает подача. Пока число присылал клиент,
 * предпросмотр спрашивали календарными днями, а заявка подавалась по рабочим, и на двух
 * неделях (14 календарных, 10 рабочих) маршруты расходились кадровым шагом (#81).
 */
export async function fetchRoute(
  employeeId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<RouteStepView[]> {
  const query = new URLSearchParams({ employeeId, from, to })
  const body = await request<{ steps: RouteStepView[] }>(
    `/hr/leave-requests/route?${query.toString()}`,
    { signal },
  )
  return body.steps
}

/**
 * Подаёт заявку на отсутствие.
 *
 * Маршрут в теле НЕ передаётся — он строится из оргструктуры на сервере. Присланный
 * клиентом маршрут означал бы возможность согласовать себе отпуск самому.
 */
export function submitLeave(payload: {
  employeeId: string
  requestedBy: string | null
  type: string
  from: string
  to: string
  comment: string | null
}): Promise<LeaveRequestCard> {
  return request<LeaveRequestCard>('/hr/leave-requests', { method: 'POST', body: payload })
}

/** Согласование. Право `approve`: одобрение списывает дни с остатка, то есть меняет учёт. */
export function approveLeave(id: string): Promise<LeaveRequestCard> {
  return request<LeaveRequestCard>(`/hr/leave-requests/${id}/approve`, { method: 'POST' })
}

/** Отказ: причина обязательна — сервер без неё отвечает 422 `leave.reason_required`. */
export function rejectLeave(id: string, reason: string): Promise<LeaveRequestCard> {
  return request<LeaveRequestCard>(`/hr/leave-requests/${id}/reject`, {
    method: 'POST',
    body: { reason },
  })
}

export function cancelLeave(id: string, on: string): Promise<LeaveRequestCard> {
  return request<LeaveRequestCard>(`/hr/leave-requests/${id}/cancel`, {
    method: 'POST',
    body: { on },
  })
}

export async function fetchBalances(
  employeeId: string,
  signal?: AbortSignal,
): Promise<BalanceView[]> {
  const body = await request<{ balances: BalanceView[] }>(
    `/hr/employees/${employeeId}/leave-balances`,
    { signal },
  )
  return body.balances
}

/** Перенос неиспользованных дней на следующий год — в свою колонку, а не в норму. */
export function carryOver(payload: {
  employeeId: string
  fromYear: number
  type: string
}): Promise<BalanceView> {
  return request<BalanceView>('/hr/leave-balances/carry-over', {
    method: 'POST',
    body: payload,
  })
}

/**
 * Кто ещё из подразделения отсутствует в эти даты.
 *
 * Это и есть то, что руководитель обязан увидеть ДО одобрения: три человека из отдела в
 * отпуске одну неделю — кадровая проблема, и она невидима, когда заявки читают по одной.
 */
export async function fetchClashes(
  departmentId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<LeaveRequestCard[]> {
  const query = new URLSearchParams({ from, to })
  const body = await request<{ requests: LeaveRequestCard[] }>(
    `/hr/departments/${departmentId}/leave-clashes?${query.toString()}`,
    { signal },
  )
  return body.requests
}

export function fetchCoverage(
  departmentId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<CoverageView> {
  const query = new URLSearchParams({ from, to })
  return request<CoverageView>(
    `/hr/departments/${departmentId}/coverage?${query.toString()}`,
    { signal },
  )
}

export async function fetchTimesheet(
  employeeId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<TimeEntryView[]> {
  const query = new URLSearchParams({ from, to })
  const body = await request<{ entries: TimeEntryView[] }>(
    `/hr/employees/${employeeId}/timesheet?${query.toString()}`,
    { signal },
  )
  return body.entries
}

// ------------------------------------------------------------------ вакансии

/*
 * `VacancyCard` здесь не объявлен намеренно: поля вакансии карточка читает через механизм
 * списков (`hr.vacancy` есть в `ListCatalog`), а второй тип на те же данные разошёлся бы
 * с каталогом молча — при первой правке колонок список и карточка показали бы разное.
 */

/** Отклик кандидата на вакансию — карточка на доске. */
export interface ApplicationCard {
  id: string
  candidateId: string
  /** Имя кандидата — под тем же правом, что открывает доску; после отзыва согласия обезличено. */
  candidateName?: string | null
  vacancyId: string
  stage: string
  rejectionReason: string | null
  hiredEmployeeId: string | null
  appliedAt: string
  stageChangedAt: string | null
  open: boolean
}

/**
 * Конверсия между двумя соседними стадиями.
 *
 * `percent` — `null`, если стадию никто не проходил. Не ноль: «все застряли» и «никто не
 * доходил» — разные факты, и ноль отвечает на первый вопрос вместо второго.
 */
export interface ConversionView {
  from: string
  to: string
  entered: number
  moved: number
  percent: number | null
}

/*
 * Чтения одной вакансии (`GET /hr/vacancies/{id}`) на сервере НЕТ — #68 вывел наружу
 * только операции и аналитику подбора. Обёртка здесь не заводится намеренно: функция,
 * которая гарантированно вернёт 404, выглядит рабочей возможностью, и через полгода на
 * неё рассчитывают. Поля вакансии карточка берёт из механизма списков (`hr.vacancy`
 * объявлена в `ListCatalog`), а воронку, конверсию и срок закрытия — вызовами ниже.
 */

export async function fetchBoard(
  vacancyId: string,
  signal?: AbortSignal,
): Promise<ApplicationCard[]> {
  const body = await request<{ applications: ApplicationCard[] }>(
    `/hr/vacancies/${vacancyId}/board`,
    { signal },
  )
  return body.applications
}

/**
 * Воронка: сколько откликов на каждой стадии.
 *
 * Отдельно от доски, хотя на первый взгляд это одно и то же. Доска показывает **открытые**
 * отклики, воронка считает **все**, включая отказы и нанятых. Собрать воронку пересчётом
 * доски значит потерять именно тех, ради кого её и смотрят: стадия, с которой все уходят
 * в отказ, на доске выглядит пустой и благополучной.
 */
export function fetchFunnel(
  vacancyId: string,
  signal?: AbortSignal,
): Promise<Record<string, number>> {
  return request<Record<string, number>>(`/hr/vacancies/${vacancyId}/funnel`, { signal })
}

export async function fetchConversion(
  vacancyId: string,
  signal?: AbortSignal,
): Promise<ConversionView[]> {
  const body = await request<{ conversions: ConversionView[] }>(
    `/hr/vacancies/${vacancyId}/conversion`,
    { signal },
  )
  return body.conversions
}

/** Срок закрытия вакансии в днях; `null`, пока она открыта. */
export function fetchTimeToFill(
  vacancyId: string,
  signal?: AbortSignal,
): Promise<{ vacancyId: string; days: number | null }> {
  return request<{ vacancyId: string; days: number | null }>(
    `/hr/vacancies/${vacancyId}/time-to-fill`,
    { signal },
  )
}

/**
 * Открыть вакансию для откликов (из черновика или с паузы).
 *
 * Дата открытия не передаётся: сервер берёт сегодняшнюю, а от неё считается срок закрытия.
 * Дата задним числом — это решение о сроке подбора, а не о кнопке.
 */
export function openVacancy(id: string): Promise<unknown> {
  return request(`/hr/vacancies/${id}/open`, { method: 'POST' })
}

/** Приостановить вакансию. Не закрытие: к приостановленной возвращаются. */
export function holdVacancy(id: string): Promise<unknown> {
  return request(`/hr/vacancies/${id}/hold`, { method: 'POST' })
}

/** Перевод отклика на другую стадию — перетаскиванием на доске. */
export function moveApplication(id: string, stage: string): Promise<ApplicationCard> {
  return request<ApplicationCard>(`/hr/applications/${id}/stage`, {
    method: 'POST',
    body: { stage },
  })
}

/** Отказ. Причина обязательна: отказ без причины нечего сказать кандидату и нечего анализировать. */
export function rejectApplication(id: string, reason: string): Promise<ApplicationCard> {
  return request<ApplicationCard>(`/hr/applications/${id}/reject`, {
    method: 'POST',
    body: { reason },
  })
}

/**
 * Отзыв согласия на обработку ПДн.
 *
 * Право `delete`, а не `update`: отозванное согласие означает, что с кандидатом больше
 * нельзя работать, и его данные подлежат удалению. Это не правка карточки.
 */
export function revokeConsent(candidateId: string): Promise<unknown> {
  return request(`/hr/candidates/${candidateId}/revoke-consent`, { method: 'POST' })
}

// ------------------------------------------------------------------ KPI

/**
 * Определение показателя.
 *
 * `derivable` — считается ли факт автоматически. Показатель без источника (маржа)
 * заполняется вручную и пересчётом не трогается: ноль читался бы как невыполнение, а не
 * как ненастроенный показатель. Обратное тоже верно — автоматическую метрику вручную
 * задать нельзя, правка дожила бы до первого пересчёта.
 */
export interface DefinitionView {
  id: string
  code: string
  name: string
  metric: string
  unit: string | null
  weight: number
  description: string | null
  derivable: boolean
}

/** Цель с текущим фактом. План и факт двумя числами: процент теряет масштаб. */
export interface TargetView {
  id: string
  kpiId: string
  employeeId: string | null
  departmentId: string | null
  periodStart: string
  periodEnd: string
  target: number | null
  actual: number | null
  completionPercent: number | null
  bonusAmount: number | null
  currency: string | null
}

/** Строка дашборда KPI. */
export interface DashboardLineView {
  code: string
  name: string
  unit: string | null
  target: number | null
  actual: number | null
  completionPercent: number | null
  weight: number
  met: boolean
}

/** Дашборд одного человека или подразделения за период. */
export interface DashboardView {
  employeeId: string | null
  departmentId: string | null
  from: string
  to: string
  overallPercent: number | null
  lines: DashboardLineView[]
}

/** Одна составляющая бонуса. */
export interface BonusLineView {
  code: string
  name: string
  weight: number
  target: number | null
  actual: number | null
  completionPercent: number | null
}

/**
 * Рассчитанный бонус вместе с числами, из которых он получился.
 *
 * Строки показываются рядом с итогом намеренно: премия без разбора — это цифра, которую
 * получатель не может проверить, и спор о ней идёт не о показателях, а о доверии.
 */
export interface BonusView {
  employeeId: string
  from: string
  to: string
  achievement: number | null
  fund: number | null
  bonus: number | null
  lines: BonusLineView[]
}

export async function fetchTargets(
  employeeId: string,
  signal?: AbortSignal,
): Promise<TargetView[]> {
  const body = await request<{ targets: TargetView[] }>(
    `/hr/kpi/employees/${employeeId}/targets`,
    { signal },
  )
  return body.targets
}

export function fetchDashboard(
  employeeId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<DashboardView> {
  const query = new URLSearchParams({ from, to })
  return request<DashboardView>(
    `/hr/kpi/employees/${employeeId}/dashboard?${query.toString()}`,
    { signal },
  )
}

/**
 * Рассчитывает бонус, ничего не записывая.
 *
 * `fund` обязателен — сервер без него отвечает 400, и подставлять умолчание здесь нельзя:
 * премиальный фонд это решение о деньгах, а не настройка экрана. «Сколько выйдет»
 * спрашивают до утверждения, и ответ на этот вопрос ничего менять не должен — начисление
 * это отдельное действие под правом `approve`.
 *
 * Веса, не дающие в сумме 1, — отказ сервера (422 `kpi.weights_do_not_sum`), а не тихая
 * нормализация: нормализовать молча значит выплатить сумму, которую никто не утверждал.
 */
export function fetchBonus(
  employeeId: string,
  from: string,
  to: string,
  fund: number,
  signal?: AbortSignal,
): Promise<BonusView> {
  const query = new URLSearchParams({ from, to, fund: String(fund) })
  return request<BonusView>(`/hr/kpi/employees/${employeeId}/bonus?${query.toString()}`, {
    signal,
  })
}

/**
 * Ручная простановка факта.
 *
 * Осмысленна только для показателя без источника (`derivable: false`): у автоматического
 * правка доживёт до первого пересчёта, и сервер её отвергнет. Экран поэтому предлагает
 * её только там, где она сработает — кнопка, которая всегда ошибается, учит игнорировать
 * ошибки.
 */
export function setActual(targetId: string, actual: number): Promise<TargetView> {
  return request<TargetView>(`/hr/kpi/targets/${targetId}/actual`, {
    method: 'POST',
    body: { actual },
  })
}

/**
 * Начисление премии за период.
 *
 * Право `approve`, а не `update`: это выплата. Сумма считается сервером из того же фонда и
 * тех же целей, что и в {@link fetchBonus}, и записывается на цели периода — бонус, который
 * не проследить обратно к цифрам, однажды оспорят.
 */
export function settleBonus(
  employeeId: string,
  payload: { from: string; to: string; fund: number },
): Promise<BonusView> {
  return request<BonusView>(`/hr/kpi/employees/${employeeId}/bonus/settle`, {
    method: 'POST',
    body: { ...payload, cap: null },
  })
}

/**
 * Пересчёт фактов целей сотрудника, покрывающих дату.
 *
 * Дата передаётся явно: пересчёт обязан зачесть тот период, о котором спрашивают, а не тот,
 * в котором случилось нажатие. Идемпотентен — факт читается из источника заново.
 */
export function recomputeEmployee(
  employeeId: string,
  on: string,
): Promise<{ changed: number; on: string }> {
  const query = new URLSearchParams({ on })
  return request<{ changed: number; on: string }>(
    `/hr/kpi/recompute/employee/${employeeId}?${query.toString()}`,
    { method: 'POST' },
  )
}

/** Все заведённые показатели — ради признака `derivable` (можно ли задать факт вручную). */
export async function fetchDefinitions(signal?: AbortSignal): Promise<DefinitionView[]> {
  const body = await request<{ definitions: DefinitionView[] }>('/hr/kpi/definitions', { signal })
  return body.definitions
}

/** Дашборд KPI подразделения за период — для цели, поставленной отделу, а не человеку. */
export function fetchDepartmentDashboard(
  departmentId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<DashboardView> {
  const query = new URLSearchParams({ from, to })
  return request<DashboardView>(
    `/hr/kpi/departments/${departmentId}/dashboard?${query.toString()}`,
    { signal },
  )
}
